import React, { useMemo } from "react";
import { usePlayerModal } from "../PlayerModalContext";
import { lookUpPlayerName } from "../playerDisplayName";
import { PickRoundMeta } from "./PickRoundMeta";
import { PlayerAvatarWithScore } from "./PlayerAvatarWithScore";
import { PlayerPickTrigger } from "./PlayerPickTrigger";
import {
    PRIZE_ODDS_FACTOR,
    entryByNameStatic,
    oddsWeightedTotalForPerson,
    sortRankingsByOddsScore,
} from "../oddsUtils";
import { oddsSectionLeaderNames } from "../sectionLeaders";
import { SectionLeaderNamePill } from "./SectionLeaderNamePill";
import { MissedCutPickCardShell } from "./MissedCutPickCardShell";
import {
    MISSED_CUT_CARD_MIN_HEIGHT_CLASS,
    MISSED_CUT_META_RESERVE_CLASS,
    PickRowAvatarPlaceholder,
} from "./PickRowAvatarPlaceholder";

export function OddsSection({ rankings, formatter, cutPickNames = [] }) {
    const { openPlayer } = usePlayerModal();
    const oddsRankings = useMemo(
        () => sortRankingsByOddsScore(rankings, entryByNameStatic),
        [rankings],
    );
    const leaderSet = useMemo(
        () => new Set(oddsSectionLeaderNames(rankings, entryByNameStatic)),
        [rankings],
    );
    const cutPickSet = useMemo(
        () => new Set(cutPickNames),
        [cutPickNames],
    );

    return (
        <section
            aria-label="Odds-weighted standings"
            className="rankings rounded-xl border border-masters-moss/30 bg-masters-cream/95 p-4 text-masters-forest shadow-lg shadow-black/20 sm:p-6"
        >
            <h2 className="mb-4 border-b border-masters-parchment pb-2 text-xs font-semibold uppercase tracking-[0.2em] text-masters-goldmuted">
                Odds
            </h2>
            {oddsRankings.map((person) => {
                const oddsTotal = oddsWeightedTotalForPerson(
                    person,
                    entryByNameStatic,
                );
                return (
                    <div key={person.name} className="mb-6 last:mb-0">
                        <div className="flex flex-row items-center justify-between gap-4 border-b border-masters-parchment/80 pb-3 pt-1">
                            <div className="min-w-0 pl-1">
                                {leaderSet.has(person.name) ? (
                                    <SectionLeaderNamePill name={person.name} />
                                ) : (
                                    <span className="text-base font-semibold">
                                        {person.name}
                                    </span>
                                )}
                            </div>
                            <div className="text-base font-semibold text-masters-moss">
                                {formatter.format(Math.floor(oddsTotal))}
                            </div>
                        </div>
                        <div className="space-y-1 pl-1 pt-2 text-sm">
                            {person.picks.map((pick) => {
                                const summ = person.summary?.find(
                                    ({ name }) => name === pick,
                                );
                                const entry = entryByNameStatic[pick];
                                const oddsLabel = entry?.odds ?? "—";
                                if (!summ && cutPickSet.has(pick)) {
                                    const mult = entry?.multiplier ?? 0;
                                    return (
                                        <div
                                            key={`${person.name}-${pick}-odds`}
                                        >
                                            <div className="pb-2">
                                                <MissedCutPickCardShell>
                                                    <div
                                                        className={`flex items-center gap-2 rounded-lg bg-masters-parchment/40 px-2 py-2 ${MISSED_CUT_CARD_MIN_HEIGHT_CLASS}`}
                                                    >
                                                        <PlayerPickTrigger
                                                            ariaLabel={`Player details: ${pick}`}
                                                            onOpen={() =>
                                                                openPlayer(pick)
                                                            }
                                                        >
                                                            <PickRowAvatarPlaceholder />
                                                        </PlayerPickTrigger>
                                                        <div className="flex min-h-0 min-w-0 flex-1 flex-col justify-center pl-1">
                                                            <PlayerPickTrigger
                                                                ariaLabel={`Player details: ${pick}`}
                                                                onOpen={() =>
                                                                    openPlayer(
                                                                        pick,
                                                                    )
                                                                }
                                                            >
                                                                <div className="font-medium text-masters-forest">
                                                                    {pick}
                                                                </div>
                                                            </PlayerPickTrigger>
                                                            <div
                                                                className={`mt-0.5 ${MISSED_CUT_META_RESERVE_CLASS}`}
                                                            >
                                                                <PickRoundMeta
                                                                    className="mt-0"
                                                                    thru={null}
                                                                    roundGrossStrokes={
                                                                        undefined
                                                                    }
                                                                />
                                                            </div>
                                                            <div
                                                                className="mt-1 flex flex-wrap items-baseline gap-x-0 text-xs leading-snug text-masters-forest/85 tabular-nums"
                                                                title="5% of prize money × odds multiplier"
                                                            >
                                                                <span className="shrink-0 pr-2.5 font-bold text-masters-moss">
                                                                    {oddsLabel}
                                                                </span>
                                                                <span className="min-w-0">
                                                                    <span className="text-masters-goldmuted">
                                                                        5% ×{" "}
                                                                    </span>
                                                                    <span>
                                                                        {formatter.format(
                                                                            0,
                                                                        )}
                                                                    </span>
                                                                    <span className="text-masters-goldmuted">
                                                                        {" "}
                                                                        ×{" "}
                                                                        {mult}{" "}
                                                                        =
                                                                    </span>{" "}
                                                                    <span className="font-semibold text-masters-moss">
                                                                        {formatter.format(
                                                                            0,
                                                                        )}
                                                                    </span>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </MissedCutPickCardShell>
                                            </div>
                                        </div>
                                    );
                                }
                                if (summ) {
                                    const mult = entry?.multiplier ?? 0;
                                    const lineValue =
                                        PRIZE_ODDS_FACTOR *
                                        summ.amount *
                                        mult;
                                    const lineFloored = Math.floor(lineValue);
                                    return (
                                        <div
                                            key={`${person.name}-${pick}-odds`}
                                        >
                                            <div className="pb-2">
                                                <div className="flex items-center gap-2 rounded-lg bg-masters-parchment/40 px-2 py-2">
                                                    <PlayerPickTrigger
                                                        ariaLabel={`Player details: ${pick}`}
                                                        onOpen={() =>
                                                            openPlayer(pick)
                                                        }
                                                    >
                                                        <PlayerAvatarWithScore
                                                            src={
                                                                summ.avatarURL
                                                            }
                                                            alt=""
                                                            scoreVsPar={
                                                                summ.scoreVsPar
                                                            }
                                                            imgClassName="h-10 w-10"
                                                        />
                                                    </PlayerPickTrigger>
                                                    <div className="min-w-0 pl-1">
                                                        <PlayerPickTrigger
                                                            ariaLabel={`Player details: ${pick}`}
                                                            onOpen={() =>
                                                                openPlayer(pick)
                                                            }
                                                        >
                                                            <div className="font-medium text-masters-forest">
                                                                {lookUpPlayerName(
                                                                    person,
                                                                    pick,
                                                                )}
                                                            </div>
                                                        </PlayerPickTrigger>
                                                        <PickRoundMeta
                                                            className="mt-0.5"
                                                            thru={
                                                                summ.roundThruLabel
                                                            }
                                                            roundGrossStrokes={
                                                                summ.completedRoundGross
                                                            }
                                                        />
                                                        <div
                                                            className="mt-1 flex flex-wrap items-baseline gap-x-0 text-xs leading-snug text-masters-forest/85 tabular-nums"
                                                            title="5% of prize money × odds multiplier"
                                                        >
                                                            <span className="shrink-0 pr-2.5 font-bold text-masters-moss">
                                                                {oddsLabel}
                                                            </span>
                                                            <span className="min-w-0">
                                                                <span className="text-masters-goldmuted">
                                                                    5% ×{" "}
                                                                </span>
                                                                <span>
                                                                    {formatter.format(
                                                                        summ.amount,
                                                                    )}
                                                                </span>
                                                                <span className="text-masters-goldmuted">
                                                                    {" "}
                                                                    × {mult} =
                                                                </span>{" "}
                                                                <span className="font-semibold text-masters-moss">
                                                                    {formatter.format(
                                                                        lineFloored,
                                                                    )}
                                                                </span>
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                                return (
                                    <div
                                        key={`${person.name}-${pick}-odds`}
                                    >
                                        <div className="rounded-lg px-2 py-1 text-masters-goldmuted">
                                            <PlayerPickTrigger
                                                ariaLabel={`Player details: ${pick}`}
                                                onOpen={() =>
                                                    openPlayer(pick)
                                                }
                                            >
                                                <div className="line-through decoration-masters-goldmuted/60">
                                                    {pick}
                                                </div>
                                            </PlayerPickTrigger>
                                            <div className="mt-0.5 text-xs not-italic text-masters-goldmuted/90">
                                                {oddsLabel}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </section>
    );
}
